import type {
  Bonus,
  Differentiator,
  Faq,
  InsideImage,
  Module,
  PopupSale,
  Testimonial,
} from '../types/content'

export const CHECKOUT_URL = 'https://cardiology-visual-atlas.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '940885339030861'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/cuwXjTBumgRtqzak861G9d/Diseno_sin_titulo-10.webp'

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/gUpRrxgefDPmQYQ8ceSvkX/Captura_de_pantalla_2026-09-23_a_la(s)_6.49.26_p._m..png',
    alt: 'Calculating heart rate — three ECG methods reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/qrRydQXFHKoJN54Jpbenzp/Captura_de_pantalla_2026-09-23_a_la(s)_6.49.47_p._m..png',
    alt: 'What are the P waves telling you — P-wave evaluation page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/wb9NworWAwFyjKQVmPpmNE/Captura_de_pantalla_2026-09-23_a_la(s)_6.50.09_p._m..png',
    alt: 'Voltage and morphology clues — LVH and RVH pattern page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/8LRCiAiKENYBBbAyLLyTdY/Captura_de_pantalla_2026-09-23_a_la(s)_6.50.28_p._m..png',
    alt: 'Sinus rhythms: same origin, different rate and pattern — comparison page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ePdGTC1MXKYnJLCUGsAaiX/Captura_de_pantalla_2026-09-23_a_la(s)_6.50.53_p._m..png',
    alt: 'Wide-complex ventricular patterns — comparison page',
  },
]

export const systemBullets = [
  'Cardiac anatomy & physiology',
  'ECG interpretation',
  'Arrhythmias & conduction disorders',
  'Cardiac pathology',
  'Cardiac imaging',
  'Clinical cardiac pattern recognition',
]

export const modules: Module[] = [
  {
    number: 1,
    title: 'CARDIAC ANATOMY & PHYSIOLOGY VISUAL ATLAS',
    body: 'Build a clearer foundation for understanding the structure and function of the heart. Explore chambers, valves, coronary circulation, major vessels, blood flow, the cardiac cycle and electrical conduction through organized diagrams, anatomical references and structured comparisons.',
    steps: 'Understand. Connect. Review.',
  },
  {
    number: 2,
    title: 'ECG INTERPRETATION VISUAL ATLAS',
    body: 'Learn to approach ECG interpretation through a clear and repeatable visual system. Explore rate, rhythm, axis, P waves, PR intervals, QRS complexes, R-wave progression, ST segments, T waves and QT intervals through annotated tracings and structured interpretation references.',
    steps: 'Read. Interpret. Recognize.',
  },
  {
    number: 3,
    title: 'ARRHYTHMIAS & CONDUCTION DISORDERS VISUAL ATLAS',
    body: 'Make cardiac rhythms easier to recognize, compare and differentiate. Explore sinus rhythms, atrial fibrillation, atrial flutter, supraventricular rhythms, ventricular rhythms, premature beats, AV blocks and bundle branch patterns through annotated ECG examples and side-by-side comparisons.',
    steps: 'Observe. Compare. Differentiate.',
  },
  {
    number: 4,
    title: 'CARDIAC PATHOLOGY VISUAL ATLAS',
    body: 'Understand how major cardiovascular conditions create recognizable patterns. Explore coronary artery disease, myocardial ischemia and infarction, heart failure, cardiomyopathies, valvular disease, pericardial disorders and other important cardiac conditions through structured visual references.',
    steps: 'Connect. Compare. Recognize.',
  },
  {
    number: 5,
    title: 'CARDIAC IMAGING VISUAL ATLAS',
    body: 'Understand cardiac anatomy and abnormalities across commonly encountered imaging modalities. Explore echocardiography, chest radiography, cardiac CT and cardiac MRI through labeled views, orientation guides, normal-versus-abnormal comparisons and structured imaging references.',
    steps: 'Identify. Compare. Understand.',
  },
  {
    number: 6,
    title: 'CLINICAL CARDIOLOGY PATTERN RECOGNITION ATLAS',
    body: 'Bring ECGs, imaging and clinical findings together through organized pattern recognition. Explore chest pain, palpitations, dyspnea, syncope, edema and other cardiovascular presentations while connecting symptoms, ECG findings, structural abnormalities and imaging clues.',
    steps: 'Assess. Connect. Recognize.',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'ECG RAPID INTERPRETATION TOOLKIT™',
    subtitle: 'A Practical Step-by-Step Reference for Systematic ECG Review',
    body: 'Follow a structured ECG review sequence covering rate, rhythm, P waves, intervals, QRS morphology, axis, R-wave progression, ST-T findings and other essential observations.',
    steps: 'Review. Interpret. Organize.',
  },
  {
    number: 2,
    title: 'CARDIAC DIFFERENTIAL PATTERN CARDS™',
    subtitle: 'Compare Common Cardiovascular Presentations Side by Side',
    body: 'Quickly compare important patterns associated with chest pain, palpitations, dyspnea, syncope, murmurs, rhythm abnormalities and other cardiovascular presentations.',
    steps: 'Compare. Differentiate. Recognize.',
  },
  {
    number: 3,
    title: 'CARDIOLOGY NORMAL vs ABNORMAL REFERENCE GUIDE™',
    subtitle: 'See the Differences That Matter',
    body: 'Compare normal cardiac findings with important abnormal patterns across ECGs, cardiac anatomy, rhythm, conduction and imaging using organized reference frameworks.',
    steps: 'Observe. Compare. Understand.',
  },
  {
    number: 4,
    title: 'CARDIOLOGY CLINICAL CASE WORKBOOK™',
    subtitle: 'Practice Integrated Cardiac Pattern Recognition',
    body: 'Work through structured educational cases combining symptoms, ECG information, cardiac findings and imaging clues while following guided interpretation frameworks.',
    steps: 'Analyze. Connect. Practice.',
  },
  {
    number: 5,
    title: 'CARDIOLOGY QUICK-REFERENCE DESK COMPANION™',
    subtitle: 'Essential Cardiology References in One Organized Resource',
    body: 'Quickly access ECG checklists, rhythm references, coronary territories, conduction patterns, valve comparisons, heart failure patterns, cardiomyopathy references and cardiac imaging orientation tools.',
    steps: 'Find. Review. Apply.',
  },
]

export const differentiators: Differentiator[] = [
  {
    icon: '❤️',
    title: 'Practical visual references',
    body: 'Find the cardiac concept, ECG pattern or imaging reference you need when studying.',
  },
  {
    icon: '🔍',
    title: 'Structured learning system',
    body: 'Move from cardiac fundamentals to ECG interpretation, pathology, imaging and integrated pattern recognition.',
  },
  {
    icon: '⚡',
    title: '6 cardiology study areas',
    body: 'Anatomy, ECGs, arrhythmias, pathology, imaging and clinical pattern recognition.',
  },
  {
    icon: '🔄',
    title: 'Ready to use again and again',
    body: 'Return to the atlas whenever you need to review a cardiac concept or compare important findings.',
  },
]

export const offerBullets = [
  '6 comprehensive modules',
  '5 strategic bonuses',
  'Cardiac anatomy & physiology',
  'ECG interpretation & rhythm recognition',
  'Cardiac pathology & conduction disorders',
  'Cardiac imaging',
  'Clinical pattern recognition',
]

export const testimonials: Testimonial[] = [
  {
    name: 'Hannah Mitchell, 23',
    role: 'Medical student, 3rd year',
    location: 'Bristol, United Kingdom',
    quote:
      'ECGs were the topic I dreaded most on rotations. Every strip looked like noise until I started using the ECG module. Going through rate, rhythm, axis and intervals in the same order every time finally stuck. Last week my consultant handed me a 12-lead on ward rounds and I spotted the inferior ST elevation before anyone else spoke.',
  },
  {
    name: 'Jordan Ellis, 26',
    role: 'Medical student preparing for USMLE Step 1',
    location: 'Philadelphia, United States',
    quote:
      'Arrhythmias were costing me points on every practice block. Atrial flutter, AF and multifocal atrial tachycardia all blurred together. The side-by-side rhythm pages and the differential pattern cards fixed that in about two weeks. My cardiology section on the practice exams went from my weakest to one of my strongest.',
  },
  {
    name: 'Priya Sharma, 24',
    role: 'Medical student, cardiology elective',
    location: 'Toronto, Canada',
    quote:
      'Echo views confused me completely. I could never tell a parasternal long axis from an apical four-chamber on the monitor. The imaging atlas labels every view with orientation guides and normal-versus-abnormal comparisons. By the end of my elective I was naming structures before the sonographer pointed them out.',
  },
  {
    name: 'Liam O’Connor, 28',
    role: 'Junior doctor, internal medicine',
    location: 'Melbourne, Australia',
    quote:
      'Night shifts leave no time to dig through textbooks. I keep the quick-reference desk companion on my phone: coronary territories, conduction blocks and QT references in seconds. It has become my go-to when I want to double-check a pattern before discussing it with the registrar.',
  },
  {
    name: 'Emma Carter, 22',
    role: 'Nursing student, cardiac unit placement',
    location: 'Leeds, United Kingdom',
    quote:
      'I started my cardiac placement feeling lost on telemetry. The sinus rhythm and ventricular pattern pages showed me exactly what to look for, with the rate and key clues in one table. Now I can explain PVCs, couplets and bradycardia to my mentor with confidence instead of guessing.',
  },
]

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Medical students, cardiology students, healthcare students, trainees and professionals reviewing cardiovascular concepts.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. CARDIOLOGY VISUAL ATLAS™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is an educational visual study and reference system.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. It focuses on organized cardiac diagrams, annotated ECGs, rhythm comparisons, imaging references and structured pattern-recognition tools.',
  },
  {
    q: 'What areas are included?',
    a: 'Cardiac anatomy and physiology, ECG interpretation, arrhythmias, conduction disorders, cardiac pathology, cardiac imaging and clinical pattern recognition.',
  },
  {
    q: 'Does this replace formal medical or cardiology training?',
    a: 'No. It is an educational study resource and does not replace formal education, supervised clinical training, institutional protocols or professional clinical judgment.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'What devices can I use it on?',
    a: 'Any device that opens PDF files — laptop, tablet, phone or desktop. You can also print the ECG and reference pages you use most.',
  },
  {
    q: 'Do I need previous cardiology knowledge?',
    a: 'No. The system starts with cardiac anatomy and physiology, then moves into ECGs, arrhythmias, pathology, imaging and clinical pattern recognition, so early students and advanced learners can both use it.',
  },
  {
    q: 'How long do I keep access, and what if it is not for me?',
    a: 'Access is yours to keep and review whenever you need it. If it is not right for you, contact us within 7 days for a full refund according to the refund terms.',
  },
]

export const popupSales: PopupSale[] = [
  { name: 'Olivia H.', city: 'London', country: 'United Kingdom' },
  { name: 'Nathan K.', city: 'Montreal', country: 'Canada' },
  { name: 'Madison R.', city: 'Boston', country: 'United States' },
  { name: 'Lucas W.', city: 'Sydney', country: 'Australia' },
  { name: 'Amelia J.', city: 'Edinburgh', country: 'United Kingdom' },
  { name: 'Ryan T.', city: 'Ottawa', country: 'Canada' },
  { name: 'Chloe B.', city: 'Houston', country: 'United States' },
  { name: 'Zoe M.', city: 'Adelaide', country: 'Australia' },
  { name: 'Harry P.', city: 'Birmingham', country: 'United Kingdom' },
  { name: 'Sophie L.', city: 'Vancouver', country: 'Canada' },
  { name: 'Ethan G.', city: 'Seattle', country: 'United States' },
  { name: 'Mia D.', city: 'Brisbane', country: 'Australia' },
]
